import { useCallback, useMemo, useState } from 'react'

import { useControllableState } from '@radix-ui/react-use-controllable-state'

import { useAppTemplates } from 'data/templates/appTemplates'
import { AppTemplate } from 'features/templates/types'
import { determineTagColor } from 'features/templates/utils'

import { ColorScheme } from 'ui/styling/baseVariables/colors/colorPalette'

type TagOption = {
    label: string
    value: string
    color: ColorScheme
}

type UseAppTemplateTagEditorStateOptions = {
    template: AppTemplate
    onChange?: (template: Partial<AppTemplate>) => void
}

export function useAppTemplateTagEditorState(options: UseAppTemplateTagEditorStateOptions) {
    const { template, onChange: providedOnChange } = options

    const { data: templates = [] } = useAppTemplates({
        refetchOnMount: 'always',
    })

    const existingTags: string[] = useMemo(() => {
        const tags = templates.reduce((acc, t) => {
            if (t.tags) {
                t.tags.forEach((tag) => acc.add(tag))
            }
            return acc
        }, new Set<string>())

        return Array.from(tags)
    }, [templates])
    const providedValue = template.tags ?? []

    const [searchQuery, setSearchQuery] = useState('')

    const existingOptions: TagOption[] = useMemo(() => {
        return existingTags.reduce((acc, tag) => {
            const normalizedTag = tag.trim().toLowerCase()
            const normalizedSearchQuery = searchQuery.trim().toLowerCase()
            if (searchQuery && !normalizedTag.includes(normalizedSearchQuery)) {
                return acc
            }

            const option = getTagOption(tag)
            if (!acc.some((o) => o.value === option.value)) {
                acc.push(option)
            }

            return acc
        }, [] as TagOption[])
    }, [existingTags, searchQuery])

    const [value = [], onChange] = useControllableState({
        prop: providedValue ?? [],
        onChange: (tags) => {
            providedOnChange?.({
                tags,
            })
        },
        defaultProp: [],
    })

    const onCheckedChange = useCallback(
        (itemValue: string, checked: boolean) => {
            if (checked) {
                onChange([...value, itemValue])
            } else {
                onChange(value.filter((iv) => iv !== itemValue))
            }
        },
        [onChange, value]
    )

    const valueSet = useMemo(() => new Set(value), [value])
    const isSelected = useCallback((itemValue: string) => valueSet.has(itemValue), [valueSet])

    const selectedOptions = useMemo(() => {
        return value.map(getTagOption)
    }, [value])

    const showAddNewTagOption = useMemo(
        () => !!searchQuery && existingOptions.length < 1,
        [searchQuery, existingOptions]
    )

    return useMemo(
        () => ({
            existingOptions,
            onCheckedChange,
            isSelected,
            selectedOptions,
            searchQuery,
            setSearchQuery,
            showAddNewTagOption,
        }),
        [
            existingOptions,
            onCheckedChange,
            isSelected,
            selectedOptions,
            searchQuery,
            setSearchQuery,
            showAddNewTagOption,
        ]
    )
}

function getTagOption(tag: string): TagOption {
    const color = determineTagColor(tag)

    return {
        label: tag,
        value: tag,
        color,
    }
}
